import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser speech-to-text (Web Speech API), defaulting to Persian (fa-IR). Turns speech into text
 * and hands each final transcript to `onText` — used to dictate into a text field (the AI entry
 * composer). Gracefully reports `supported: false` where the API is absent (e.g. Firefox).
 * The messaging voice NOTE uses useVoiceRecorder instead — this one produces text, not audio.
 */
export interface Dictation {
  supported: boolean;
  listening: boolean;
  start: () => void;
  stop: () => void;
}

interface RecognitionResult {
  0: { transcript: string };
  isFinal: boolean;
}
interface RecognitionEvent {
  results: ArrayLike<RecognitionResult>;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: RecognitionEvent) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type WithRecognition = typeof window & {
  SpeechRecognition?: new () => SpeechRecognitionLike;
  webkitSpeechRecognition?: new () => SpeechRecognitionLike;
};

export function useSpeechDictation(onText: (text: string) => void, lang = 'fa-IR'): Dictation {
  const Ctor =
    typeof window !== 'undefined'
      ? ((window as WithRecognition).SpeechRecognition ??
        (window as WithRecognition).webkitSpeechRecognition)
      : undefined;
  const supported = !!Ctor;
  const [listening, setListening] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const stop = useCallback(() => {
    recRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    if (!Ctor || recRef.current) return;
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (e) => {
      let text = '';
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i];
        if (r?.isFinal) text += r[0].transcript;
      }
      if (text.trim()) onTextRef.current(text.trim());
    };
    rec.onerror = () => {};
    rec.onend = () => {
      recRef.current = null;
      setListening(false);
    };
    recRef.current = rec;
    setListening(true);
    try {
      rec.start();
    } catch {
      recRef.current = null;
      setListening(false);
    }
  }, [Ctor, lang]);

  useEffect(
    () => () => {
      try {
        recRef.current?.abort();
      } catch {
        // ignore
      }
    },
    [],
  );

  return { supported, listening, start, stop };
}
